import { z } from "zod";
import { ProjectRoleEnum, FeatureTypeEnum } from "@/types/enum";

export const LoginDtoSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const RegisterDtoSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string(),
});

export const ProjectDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
});

export const CreateProjectDtoSchema = z.object({
  name: z.string(),
  description: z.string(),
});

export const UpdateProjectDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  clearanceDistance: z.number().optional().default(3),
  datasetIds: z.array(z.string()).optional(),
});

export const DeleteProjectDtoSchema = z.object({
  id: z.string(),
});

export const ProjectUserAssignmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.nativeEnum(ProjectRoleEnum),
  joinedAt: z.string(), // This is the assignment date
});

export const GetProjectUsersResponseSchema = z.object({
  assignedUsers: z.array(ProjectUserAssignmentSchema),
});

export const AssignUsersToProjectDtoSchema = z.object({
  projectId: z.string(),
  assignments: z.array(
    z.object({
      userId: z.string(),
      role: z.nativeEnum(ProjectRoleEnum),
    })
  ),
});

export const UnassignUsersFromProjectDtoSchema = z.object({
  projectId: z.string(),
  userIds: z.array(z.string()),
});

export const UpdateUserRoleDtoSchema = z.object({
  projectId: z.string(),
  userId: z.string(),
  role: z.nativeEnum(ProjectRoleEnum),
});

export const TransferOwnershipDtoSchema = z.object({
  projectId: z.string(),
  currentUserId: z.string(),
  targetUserId: z.string(),
});

export const ForgotPasswordDtoSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const ResetPasswordDtoSchema = z
  .object({
    token: z.string().min(1, "Reset token is required"),
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string().min(8, "Password confirmation is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const UpdateProfileDtoSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
});

export const ChangePasswordDtoSchema = z
  .object({
    currentPassword: z.string().min(8, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "New password must be at least 8 characters long"),
    confirmNewPassword: z.string().min(8, "Password confirmation is required"),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "New passwords don't match",
    path: ["confirmNewPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "New password must be different from current password",
    path: ["newPassword"],
  });

export const ConfirmEmailDtoSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const CreateFeatureDtoSchema = z.object({
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
  projectId: z.string(),
});

export const UpdateFeatureDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
});

export const UpdateBuildingFeatureDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
  file: z.instanceof(File).optional(),
});

export const UpdatePolygonFeatureDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
});

export const UpdateMeasureFeatureDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
});

export const UpdateTileOverlayFeatureDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(FeatureTypeEnum),
  coordinates: z.array(z.array(z.number())),
  properties: z.record(z.string(), z.any()),
  config: z.record(z.string(), z.any()),
});

export const CloneProjectDtoSchema = z.object({
  projectId: z.string(),
  name: z.string(),
});
